import { useState, useEffect } from 'react';
import {
  Paper,
  Stack,
  Text,
  Divider,
  Switch,
  NumberInput,
  Select,
  Group,
  Button,
  Badge,
  Table,
  ActionIcon,
  TextInput,
  Modal,
  Loader,
  Center,
  Alert,
  Tooltip,
  Collapse,
} from '@mantine/core';
import {
  IconMail,
  IconClock,
  IconSend,
  IconTrash,
  IconPlus,
  IconUserPlus,
  IconInfoCircle,
  IconClipboardCheck,
  IconShieldCheck,
  IconTool,
  IconCalendarEvent,
} from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { useOrganization } from '@/hooks/useOrganization';
import {
  useNotificationSettings,
  useUpdateNotificationSettings,
  useNotificationLogs,
  useTriggerNotifications,
  NotificationRecipient,
} from '@/hooks/api/useNotificationSettings';
import { useOrganizationMembers } from '@/hooks/api/useOrganizationMembers';

const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'UTC', label: 'UTC' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
];

const FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'never', label: 'Never' },
];

export function NotificationSettings() {
  const { organizationId } = useOrganization();
  const { data: settings, isLoading, error } = useNotificationSettings(organizationId || '');
  const { data: logs, isLoading: logsLoading } = useNotificationLogs(organizationId || '', 20);
  const { data: members } = useOrganizationMembers(organizationId || '');
  const updateSettings = useUpdateNotificationSettings();
  const triggerNotifications = useTriggerNotifications();
  const [addRecipientOpened, { open: openAddRecipient, close: closeAddRecipient }] = useDisclosure(false);
  const [showLogs, setShowLogs] = useState(false);

  // Local state for form
  const [localSettings, setLocalSettings] = useState<any>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  useEffect(() => {
    if (settings) {
      setLocalSettings(settings);
    }
  }, [settings]);

  const handleChange = (field: string, value: any) => {
    setLocalSettings((prev: any) => ({
      ...prev,
      [field]: value,
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    if (!organizationId || !localSettings) return;
    updateSettings.mutate({
      organizationId,
      settings: localSettings,
    });
    setHasChanges(false);
  };

  const handleReset = () => {
    setLocalSettings(settings);
    setHasChanges(false);
  };

  const handleAddRecipient = (type: 'user' | 'email') => {
    if (type === 'email' && newEmail) {
      const recipients = [...(localSettings?.notification_recipients || [])];
      recipients.push({ type: 'email', email: newEmail });
      handleChange('notification_recipients', recipients);
      setNewEmail('');
      closeAddRecipient();
    } else if (type === 'user' && selectedUserId) {
      const member = members?.find((m) => m.user_id === selectedUserId);
      const recipients = [...(localSettings?.notification_recipients || [])];
      recipients.push({
        type: 'user',
        user_id: selectedUserId,
        name: member?.profile?.display_name || member?.profile?.email,
      });
      handleChange('notification_recipients', recipients);
      setSelectedUserId(null);
      closeAddRecipient();
    }
  };

  const handleRemoveRecipient = (index: number) => {
    const recipients = [...(localSettings?.notification_recipients || [])];
    recipients.splice(index, 1);
    handleChange('notification_recipients', recipients);
  };

  const handleTriggerNotifications = () => {
    if (organizationId) {
      triggerNotifications.mutate(organizationId);
    }
  };

  if (isLoading) {
    return (
      <Center h={200}>
        <Loader />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert color="red" title="Error loading notification settings">
        {error.message}
      </Alert>
    );
  }

  if (!localSettings) return null;

  const recipients: NotificationRecipient[] = localSettings.notification_recipients || [];

  return (
    <Stack gap="lg">
      {/* Audit Notifications */}
      <Paper withBorder p="md">
        <Stack gap="md">
          <Group>
            <IconClipboardCheck size={20} color="var(--mantine-color-blue-6)" />
            <div>
              <Text fw={600} size="lg">
                Audit Notifications
              </Text>
              <Text c="dimmed" size="sm">
                Get notified about upcoming and overdue audits
              </Text>
            </div>
          </Group>

          <Divider />

          <Group justify="space-between">
            <div>
              <Text size="sm" fw={500}>
                Audit Reminders
              </Text>
              <Text size="xs" c="dimmed">
                Receive reminders before audit due dates
              </Text>
            </div>
            <Switch
              checked={localSettings.audit_reminder_enabled}
              onChange={(e) => handleChange('audit_reminder_enabled', e.currentTarget.checked)}
            />
          </Group>

          {localSettings.audit_reminder_enabled && (
            <NumberInput
              label="Days before due date"
              description="Send reminder this many days before the audit is due"
              value={localSettings.audit_reminder_days_before}
              onChange={(value) => handleChange('audit_reminder_days_before', value)}
              min={1}
              max={90}
              step={1}
              w={200}
            />
          )}

          <Divider variant="dashed" />

          <Group justify="space-between">
            <div>
              <Text size="sm" fw={500}>
                Overdue Audit Alerts
              </Text>
              <Text size="xs" c="dimmed">
                Receive alerts when audits are past due
              </Text>
            </div>
            <Switch
              checked={localSettings.audit_overdue_enabled}
              onChange={(e) => handleChange('audit_overdue_enabled', e.currentTarget.checked)}
            />
          </Group>

          {localSettings.audit_overdue_enabled && (
            <Select
              label="Alert frequency"
              description="How often to send overdue audit reminders"
              value={localSettings.audit_overdue_frequency}
              onChange={(value) => handleChange('audit_overdue_frequency', value)}
              data={FREQUENCY_OPTIONS}
              w={200}
            />
          )}
        </Stack>
      </Paper>

      {/* Warranty Notifications */}
      <Paper withBorder p="md">
        <Stack gap="md">
          <Group>
            <IconShieldCheck size={20} color="var(--mantine-color-orange-6)" />
            <div>
              <Text fw={600} size="lg">
                Warranty Notifications
              </Text>
              <Text c="dimmed" size="sm">
                Get notified before asset warranties expire
              </Text>
            </div>
          </Group>

          <Divider />

          <Group justify="space-between">
            <div>
              <Text size="sm" fw={500}>
                Warranty Expiration Alerts
              </Text>
              <Text size="xs" c="dimmed">
                Receive alerts before warranties expire
              </Text>
            </div>
            <Switch
              checked={localSettings.warranty_expiring_enabled}
              onChange={(e) => handleChange('warranty_expiring_enabled', e.currentTarget.checked)}
            />
          </Group>

          {localSettings.warranty_expiring_enabled && (
            <NumberInput
              label="Days before expiration"
              description="Send alert this many days before warranty expires"
              value={localSettings.warranty_expiring_days_before}
              onChange={(value) => handleChange('warranty_expiring_days_before', value)}
              min={1}
              max={180}
              step={1}
              w={200}
            />
          )}
        </Stack>
      </Paper>

      {/* Maintenance Notifications */}
      <Paper withBorder p="md">
        <Stack gap="md">
          <Group>
            <IconTool size={20} color="var(--mantine-color-cyan-6)" />
            <div>
              <Text fw={600} size="lg">
                Maintenance Notifications
              </Text>
              <Text c="dimmed" size="sm">
                Get notified about upcoming asset maintenance
              </Text>
            </div>
          </Group>

          <Divider />

          <Group justify="space-between">
            <div>
              <Text size="sm" fw={500}>
                Maintenance Due Alerts
              </Text>
              <Text size="xs" c="dimmed">
                Receive alerts when maintenance is due
              </Text>
            </div>
            <Switch
              checked={localSettings.maintenance_due_enabled}
              onChange={(e) => handleChange('maintenance_due_enabled', e.currentTarget.checked)}
            />
          </Group>

          {localSettings.maintenance_due_enabled && (
            <NumberInput
              label="Days before due date"
              description="Send alert this many days before maintenance is due"
              value={localSettings.maintenance_due_days_before}
              onChange={(value) => handleChange('maintenance_due_days_before', value)}
              min={1}
              max={90}
              step={1}
              w={200}
            />
          )}
        </Stack>
      </Paper>

      {/* Recipients */}
      <Paper withBorder p="md">
        <Stack gap="md">
          <Group justify="space-between">
            <Group>
              <IconMail size={20} color="var(--mantine-color-green-6)" />
              <div>
                <Text fw={600} size="lg">
                  Notification Recipients
                </Text>
                <Text c="dimmed" size="sm">
                  Who should receive these notifications
                </Text>
              </div>
            </Group>
            <Button
              size="xs"
              variant="light"
              leftSection={<IconPlus size={14} />}
              onClick={openAddRecipient}
            >
              Add Recipient
            </Button>
          </Group>

          <Divider />

          {recipients.length === 0 ? (
            <Alert variant="light" color="blue" icon={<IconInfoCircle size={16} />}>
              No recipients configured. Notifications will be sent to all organization admins by
              default.
            </Alert>
          ) : (
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Recipient</Table.Th>
                  <Table.Th w={50}></Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {recipients.map((recipient, index) => (
                  <Table.Tr key={index}>
                    <Table.Td>
                      <Badge size="sm" variant="light" color={recipient.type === 'user' ? 'blue' : 'green'}>
                        {recipient.type === 'user' ? 'Team Member' : 'Email'}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {recipient.type === 'email' ? recipient.email : recipient.name || recipient.user_id}
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon
                        size="sm"
                        color="red"
                        variant="subtle"
                        onClick={() => handleRemoveRecipient(index)}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      </Paper>

      {/* Schedule Settings */}
      <Paper withBorder p="md">
        <Stack gap="md">
          <Group>
            <IconClock size={20} color="var(--mantine-color-violet-6)" />
            <div>
              <Text fw={600} size="lg">
                Schedule & Timezone
              </Text>
              <Text c="dimmed" size="sm">
                Configure when notifications are sent
              </Text>
            </div>
          </Group>

          <Divider />

          <Select
            label="Timezone"
            description="Timezone used for scheduling notifications"
            value={localSettings.timezone}
            onChange={(value) => handleChange('timezone', value)}
            data={TIMEZONE_OPTIONS}
            w={300}
          />

          <Group justify="space-between" mt="sm">
            <div>
              <Text size="sm" fw={500}>
                Daily Digest
              </Text>
              <Text size="xs" c="dimmed">
                Combine all notifications into a single daily email
              </Text>
            </div>
            <Switch
              checked={localSettings.daily_digest_enabled}
              onChange={(e) => handleChange('daily_digest_enabled', e.currentTarget.checked)}
            />
          </Group>
        </Stack>
      </Paper>

      {/* Test & Logs */}
      <Paper withBorder p="md">
        <Stack gap="md">
          <Group justify="space-between">
            <Group>
              <IconCalendarEvent size={20} color="var(--mantine-color-gray-6)" />
              <div>
                <Text fw={600} size="lg">
                  Test & History
                </Text>
                <Text c="dimmed" size="sm">
                  Trigger a test notification or view sent history
                </Text>
              </div>
            </Group>
            <Group>
              <Button
                size="xs"
                variant="light"
                onClick={() => setShowLogs(!showLogs)}
              >
                {showLogs ? 'Hide' : 'Show'} History
              </Button>
              <Tooltip label="This will check for any due notifications and send them now">
                <Button
                  size="xs"
                  leftSection={<IconSend size={14} />}
                  onClick={handleTriggerNotifications}
                  loading={triggerNotifications.isPending}
                >
                  Send Test
                </Button>
              </Tooltip>
            </Group>
          </Group>

          <Collapse in={showLogs}>
            <Divider mb="md" />
            {logsLoading ? (
              <Center h={100}>
                <Loader size="sm" />
              </Center>
            ) : logs && logs.length > 0 ? (
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Date</Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Subject</Table.Th>
                    <Table.Th>Status</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {logs.map((log) => (
                    <Table.Tr key={log.id}>
                      <Table.Td>
                        <Text size="xs">
                          {new Date(log.sent_at).toLocaleString()}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge size="xs" variant="light">
                          {log.notification_type.replace(/_/g, ' ')}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Text size="xs" lineClamp={1}>
                          {log.subject}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          size="xs"
                          color={log.status === 'sent' ? 'green' : log.status === 'failed' ? 'red' : 'gray'}
                        >
                          {log.status}
                        </Badge>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            ) : (
              <Text size="sm" c="dimmed" ta="center" py="md">
                No notification history yet
              </Text>
            )}
          </Collapse>
        </Stack>
      </Paper>

      {/* Save Button */}
      {hasChanges && (
        <Group justify="flex-end">
          <Button variant="subtle" onClick={handleReset}>
            Reset
          </Button>
          <Button onClick={handleSave} loading={updateSettings.isPending}>
            Save Changes
          </Button>
        </Group>
      )}

      {/* Add Recipient Modal */}
      <Modal opened={addRecipientOpened} onClose={closeAddRecipient} title="Add Notification Recipient">
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Add a team member or external email address to receive notifications.
          </Text>

          <Divider label="Add Team Member" labelPosition="center" />

          <Select
            label="Select team member"
            placeholder="Choose a team member"
            value={selectedUserId}
            onChange={setSelectedUserId}
            data={
              members?.map((m) => ({
                value: m.user_id,
                label: m.profile?.display_name || m.profile?.email || m.user_id,
              })) || []
            }
            searchable
            clearable
          />

          <Button
            variant="light"
            leftSection={<IconUserPlus size={16} />}
            onClick={() => handleAddRecipient('user')}
            disabled={!selectedUserId}
          >
            Add Team Member
          </Button>

          <Divider label="Or Add Email" labelPosition="center" />

          <TextInput
            label="Email address"
            placeholder="email@example.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.currentTarget.value)}
            type="email"
          />

          <Button
            variant="light"
            leftSection={<IconMail size={16} />}
            onClick={() => handleAddRecipient('email')}
            disabled={!newEmail || !newEmail.includes('@')}
          >
            Add Email
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
